/**
 * ComposerInput, chat composer at the bottom of the page.
 *
 * Universal chat conventions: input at the bottom, Enter to send,
 * Shift+Enter for newline, send button activates when input non-empty.
 *
 * Sized so the input + chips above never push the last assistant
 * message off-screen on mobile (1-thumb-1-screen per Wroblewski).
 */

// MS20: ArrowUp (chat-style Heidi send affordance) replaces
// ArrowRight, mirroring the MS11 SmartIntentBar swap on the landing
// hero.
// MS57: ThinkingStrip ("alive-mascot") used to render here below
// the composer when the LLM was generating the next assistant turn.
// MS203 moved that affordance INTO the chat thread (as ThinkingBubble
// at the position the assistant's next message will appear) per
// Lucky's design call. The in-button Loader2 stays — it's the
// universal "your message is in flight" affordance every chat ships,
// removing it would make users wonder if their message even sent.
// MS505: voice-input mic button removed per Lucky ("please hide the
// mic buttons from the chat widgets"). The Web Speech API plumbing,
// permission-prompt flow, voice state, pulse animation, and error
// display all go away with it — they were only used by the mic
// affordance, so leaving them as dead code would just add weight
// to the bundle and ESLint noise. The hero SmartIntentBar still has
// its own mic (separate component, separate surface) — touch that
// only if Lucky says so too.
import { ArrowUp, Loader2, Square } from "lucide-react";
import { KeyboardEvent, useRef, useState, useEffect } from "react";

interface ComposerInputProps {
  onSubmit: (text: string) => void;
  disabled?: boolean;
  showStop?: boolean;
  onStop?: () => void;
  prefillKey?: number;
  prefill?: string;
  placeholder?: string;
  maxLength?: number;
  // MS203: `recipientFirstName` prop removed — was only consumed by
  // the ThinkingStrip that used to render below the composer. The
  // alive-mascot now lives inside the chat thread (ThinkingBubble in
  // InlineConcierge.tsx + Chat.tsx) and reads the recipient name
  // from the consuming surface directly, so ComposerInput no longer
  // needs to receive or thread it through.
}

const DEFAULT_PLACEHOLDER = "Tell us more…";

export function ComposerInput({
  onSubmit,
  disabled = false,
  showStop = false,
  onStop,
  prefillKey = 0,
  prefill = "",
  placeholder = DEFAULT_PLACEHOLDER,
  maxLength = 1000,
}: ComposerInputProps) {
  const [text, setText] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!prefillKey) return;
    setText(prefill);
    requestAnimationFrame(() => ref.current?.focus());
  }, [prefillKey, prefill]);

  // Auto-grow the textarea up to ~5 lines, then scroll.
  //
  // MS193: the lower bound is governed by the textarea's CSS
  // min-h-[36px] sm:min-h-[42px] (matches the send button row).
  // When scrollHeight is below that floor, CSS min-height still
  // wins over the inline `style.height` we set here. When content
  // grows past the floor, this effect bumps the inline height to
  // match content (up to 140px = ~5 lines, then internal scroll).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [text]);

  const canSend = text.trim().length > 0 && !disabled;

  const handleSubmit = () => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSubmit(trimmed);
    setText("");
    // Re-focus for follow-up typing
    requestAnimationFrame(() => ref.current?.focus());
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // MS506: render as a real <form> + circular coral send button —
  // matches the catering DashboardConciergeChat composer pattern
  // exactly (rounded-full 40x40 coral button, Enter-to-submit
  // semantics via form onSubmit, navy/8 border instead of the
  // charcoal/15 border-2). Pre-MS506 we were a <div> with
  // rounded-xl button, which read more like a form input than a
  // messaging composer. Same visual rhythm the catering platform
  // ships.
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      className="space-y-2"
    >
      <div className="flex items-end gap-2 rounded-2xl border border-navy/12 bg-white px-4 py-2.5 shadow-sm focus-within:border-coral">
        <textarea
          ref={ref}
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label="Type your message"
          disabled={disabled}
          maxLength={maxLength}
          /* MS193: min-h matches the send button row so the
             empty/single-line state fills the row's height and the
             placeholder appears flush at the top instead of dangling
             at the bottom (which is what items-end was doing when the
             textarea was shorter than the button row). px-0 because
             the parent already pads; leading-relaxed to give the
             content a comfortable line-height inside the taller box. */
          className="flex-1 resize-none bg-transparent text-base leading-relaxed text-navy outline-none placeholder:text-charcoal/45 disabled:opacity-60 min-h-[36px] sm:min-h-[42px] pt-1"
          style={{ fontFamily: "DM Sans, sans-serif", maxHeight: 140 }}
        />
        <button
          type={showStop ? "button" : "submit"}
          disabled={showStop ? false : !canSend}
          aria-label={showStop ? "Stop generating" : "Send message"}
          onClick={showStop ? onStop : undefined}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-coral text-white transition-colors hover:bg-coral/90 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2"
        >
          {showStop ? (
            <Square className="h-3.5 w-3.5 fill-current" strokeWidth={0} />
          ) : disabled ? (
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.25} />
          ) : (
            <ArrowUp className="h-4 w-4" strokeWidth={2.25} />
          )}
        </button>
      </div>
      {/* MS57: alive-mascot thinking strip used to render here below
          the input. MS203 removed it. Lucky's direction:
          "The Thinking Animations should probably replace the
          current three-dot loading animation inside the chat
          widget. I think that that's the best place to put it."

          The thinking affordance now mounts as a <ThinkingBubble>
          inside the chat thread (at the position the assistant's
          next message will appear) — see InlineConcierge.tsx and
          Chat.tsx where ThinkingBubble replaces ChatTypingIndicator. */}
      {/* MS186: character counter, only surfaces when the user is
          within 100 chars of the cap. Keeps the bar clean for the
          99% of messages that are well under 1000 chars, gives a
          gentle heads-up before the textarea silently rejects the
          next keystroke at maxLength. aria-live="polite" announces
          the count to screen-readers without interrupting the
          composition flow. Coral when ≤20 chars remain (matches the
          send-button accent so the user instinctively reads it as
          "approaching limit"). */}
      {text.length >= maxLength - 100 && (
        <p
          role="status"
          aria-live="polite"
          className={`px-1 text-right text-xs tabular-nums ${
            maxLength - text.length <= 20 ? "text-coral font-medium" : "text-charcoal/55"
          }`}
          style={{ fontFamily: "DM Sans, sans-serif" }}
        >
          {maxLength - text.length} chars left
        </p>
      )}
    </form>
  );
}
